// Files Data Base — a thumbnail that is actually a thumbnail (P3 Stage 2).
// 2026-10-27: the server-side resize (Supabase Image Transformation) is GONE — it is billed per
// image and this project never uses it (CLAUDE.md "Performance Rules"). A thumbnail is now the
// original, fetched through the plain authenticated object route (the session's JWT, the same RLS
// as any other read, no per-image charge) and shrunk in the browser by thumbResize.js. The bytes
// are shown through an object URL (an <img src> cannot carry the auth header), and three rules
// keep the storm from coming back:
//   • only what is on screen is asked for — the observer in FilePreview, never the row list;
//   • at most SIX in flight, the rest queued in the order they became visible;
//   • every thumbnail is remembered for the session (a bounded cache; the oldest object URL
//     is revoked when it is full), so scrolling back asks the server for nothing — and on this
//     device for good (thumbStore.js: the shrunk bytes live in the browser, per user).
// The shrink is contain: the photo is never cropped, here or anywhere in this module.
//
// P4: the same loader serves the model cards and the folder gallery at THEIR drawn sizes — a
// size rides on the request and on the cache key, so a card never shows a table thumbnail
// blown up, and a table never shows a card.
import { supabase } from '../../../supabase';
import { FILES_BUCKET } from '../filesConstants';
import { resizeBlob } from './thumbResize';
import { readThumb, writeThumb, clearThumbs, purgeOthers } from './thumbStore';

export const THUMB_W = 88;      // 2× the 44 × 32 cell, for a sharp thumbnail on a retina screen
export const THUMB_H = 64;
const IN_FLIGHT = 6;
const CACHE_MAX = 3000;         // ~2–25 KB each → tens of MB of thumbnails at most
const BASE = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

const cache = new Map();        // path → object URL (Map keeps insertion order — the oldest is first)
const pending = new Map();      // path → Promise<url|null>
const queue = [];               // paths waiting for a slot, in order of request
let inFlight = 0;

export const SIZES = { cell: { w: THUMB_W, h: THUMB_H }, card: { w: 392, h: 294 }, tile: { w: 424, h: 283 }, sugg: { w: 92, h: 64 } };   // 2× the drawn box, for a retina screen
const originalUrl = (path) => `${BASE}/storage/v1/object/authenticated/${FILES_BUCKET}/${path}`;   // the plain read — no transformation
const keyOf = (path, size) => `${size.w}x${size.h}:${path}`;

async function session() {
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

let guardedFor = null;
let listening = false;
function guardKept(owner) {   // best-effort: sign-out clears, and the next user to open Files removes what is not theirs
  if (guardedFor === owner) return;
  guardedFor = owner;
  purgeOthers(owner);
  if (listening) return;
  listening = true;
  try { supabase.auth.onAuthStateChange((event) => { if (event === 'SIGNED_OUT') { guardedFor = null; clearThumbs(); } }); } catch (_) { listening = false; }
}
function remember(key, url) {
  if (cache.size >= CACHE_MAX) { const [oldest, oldUrl] = cache.entries().next().value; cache.delete(oldest); try { URL.revokeObjectURL(oldUrl); } catch (_) { /* already gone */ } }
  cache.set(key, url);
}

async function fetchOne(path, size) {
  const s = await session();
  const t = s?.access_token;
  if (!t) return null;
  const owner = s.user?.id;
  guardKept(owner);
  const kept = await readThumb(owner, path, size);   // this device already downloaded it once
  if (kept) { const url = URL.createObjectURL(kept); remember(keyOf(path, size), url); return url; }
  const r = await fetch(originalUrl(path), { headers: { apikey: ANON, Authorization: `Bearer ${t}` } });
  if (!r.ok) return null;
  const small = await resizeBlob(await r.blob(), size);
  writeThumb(owner, path, size, small);
  const url = URL.createObjectURL(small);
  remember(keyOf(path, size), url);
  return url;
}

function settle(key, resolve, url) { inFlight -= 1; pending.delete(key); resolve(url); pump(); }
function take() {
  const { key, path, size, resolve } = queue.shift();
  inFlight += 1;
  fetchOne(path, size).catch(() => null).then((url) => settle(key, resolve, url));
}
function pump() { while (inFlight < IN_FLIGHT && queue.length) take(); }

/** The object URL of a path's thumbnail — from the cache at once, else queued. null when it cannot be had. */
export function thumbFor(path, size = SIZES.cell) {
  if (!path) return Promise.resolve(null);
  const key = keyOf(path, size);
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  if (pending.has(key)) return pending.get(key);
  const p = new Promise((resolve) => { queue.push({ key, path, size, resolve }); pump(); });
  pending.set(key, p);
  return p;
}

/** Already known, synchronously — a mounted row paints without a tick of blank. */
export const cachedThumb = (path, size = SIZES.cell) => (path && cache.get(keyOf(path, size))) || null;

/** What the cache and the queue hold — the probe reads it; nothing in the UI does. */
export const thumbStats = () => ({ cached: cache.size, pending: pending.size, queued: queue.length, inFlight });
